import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

import { api, getToken, setToken } from './api'
import type { MyPermissions, User } from './types'

interface AuthState {
  user: User | null
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  /** Вход по одноразовому коду: токен уже получен, остаётся его принять. */
  loginWithToken: (token: string, user: User) => Promise<void>
  /** Вход по коду устройства: сервер вернул только токен, кто это — спросим сами. */
  adoptToken: (token: string) => Promise<void>
  logout: () => void
  canSeeAll: boolean
  isAdmin: boolean
  /** Есть ли у текущей группы такое право. */
  can: (code: string) => boolean
  permissions: MyPermissions | null
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [perms, setPerms] = useState<MyPermissions | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!getToken()) {
      setLoading(false)
      return
    }
    Promise.all([api.me(), api.myPermissions()])
      .then(([me, p]) => {
        setUser(me)
        setPerms(p)
      })
      .catch(() => setToken(null))
      .finally(() => setLoading(false))
  }, [])

  // Токен протух на сервере — выкидываем на форму входа, а не показываем пустые экраны
  useEffect(() => {
    const onUnauthorized = () => {
      setUser(null)
      setPerms(null)
    }
    window.addEventListener('timetrack:unauthorized', onUnauthorized)
    return () => window.removeEventListener('timetrack:unauthorized', onUnauthorized)
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    const res = await api.login(email, password)
    setToken(res.access_token)
    setUser(res.user)
    setPerms(await api.myPermissions())
  }, [])

  const loginWithToken = useCallback(async (token: string, who: User) => {
    setToken(token)
    setUser(who)
    setPerms(await api.myPermissions())
  }, [])

  const adoptToken = useCallback(async (token: string) => {
    setToken(token)
    const [me, p] = await Promise.all([api.me(), api.myPermissions()])
    setUser(me)
    setPerms(p)
  }, [])

  const logout = useCallback(() => {
    setToken(null)
    setUser(null)
    setPerms(null)
  }, [])

  const value = useMemo<AuthState>(() => {
    // Пока права не загрузились, ничего лишнего не показываем
    const can = (code: string) =>
      !!perms && (perms.is_admin || perms.permissions.includes(code))
    return {
      user,
      loading,
      login,
      loginWithToken,
      adoptToken,
      logout,
      permissions: perms,
      can,
      canSeeAll: perms?.task_scope === 'all',
      isAdmin: !!perms?.is_admin,
    }
  }, [user, perms, loading, login, loginWithToken, adoptToken, logout])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth должен вызываться внутри AuthProvider')
  return ctx
}
